import { SignedIn, UserButton } from "@daveyplate/better-auth-ui"
import { t } from "@lingui/core/macro"
import { Link, useLocation, useRouterState } from "@tanstack/react-router"
import {
  Home,
  Settings,
  Menu,
  Sun,
  Moon,
  Monitor,
  Shield,
  FileStack,
  HelpCircle,
  FileBarChart,
  Layers,
  ClipboardList,
  Search,
  X,
} from "lucide-react"
import { useTheme } from "next-themes"
import { useState, useEffect, useMemo, useCallback } from "react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useTypedFlag } from "@/features/feature-flags/registry"
import { LIST_OF_MATERIALS_IN_NAVIGATION } from "@/features/list-of-materials/navigation"
import { usePermissions } from "@/hooks/usePermissions"
import { cn } from "@/lib/utils"
import { BrandLogo } from "./BrandLogo"

// Human-readable role names
const roleDisplayNames: Record<string, string> = {
  admin: "Administrator",
  blueprint_planner: "Blueprint Planner",
  blueprint_reviewer: "Blueprint Reviewer",
  store_designer: "Store Designer",
  store_manager: "Store Manager",
  external_viewer: "External Viewer",
}

export function Header({ className }: { className?: string }) {
  const location = useLocation()
  const pathname = location.pathname
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const { role, hasPermission } = usePermissions()
  const showListOfMaterials =
    LIST_OF_MATERIALS_IN_NAVIGATION && hasPermission("configVersion", "read").allowed
  const { value: showFiles } = useTypedFlag("show-files")
  const { value: showLayoutExplorer } = useTypedFlag("layout-explorer")
  const { value: showBulkUpdates } = useTypedFlag("layout-explorer-change-requests")
  const { value: darkModeEnabled } = useTypedFlag("dark-mode")
  const isNavigating = useRouterState({ select: (s) => s.isLoading })

  // Close mobile menu on route change
  useEffect(() => {
    setShowMobileMenu(false)
  }, [pathname])

  // Handle click outside to close menu
  const handleBackdropClick = useCallback(() => {
    setShowMobileMenu(false)
  }, [])

  // Handle escape key to close menu
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showMobileMenu) {
        setShowMobileMenu(false)
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [showMobileMenu])

  // Build additional links for user button
  const userButtonLinks = useMemo(() => {
    const links: Array<{
      href: string
      icon?: React.ReactNode
      label: React.ReactNode
      signedIn?: boolean
      separator?: boolean
    }> = []

    // Show role as a non-clickable info item
    if (role) {
      links.push({
        href: "#",
        icon: <Shield className="h-4 w-4" />,
        label: <span className="text-muted-foreground">{roleDisplayNames[role] || role}</span>,
        signedIn: true,
        separator: true,
      })
    }

    return links
  }, [role])

  return (
    <header
      className={cn(
        "border-brand-darkblue-500 dark:border-brand-darkblue-300 dark:bg-brand-darkblue-900 sticky top-0 z-20 border-b-2 bg-white transition-colors duration-200 overflow-x-hidden",
        className,
      )}
    >
      {/* Navigation progress bar */}
      <NavigationProgress isNavigating={isNavigating} />
      <div className="w-full px-4">
        <div className="flex h-16 items-center justify-between min-w-0">
          <div className="flex items-center space-x-6 min-w-0 shrink">
            <Link to="/" className="shrink-0">
              <BrandLogo variant="primary" size="md" />
            </Link>
            <div className="hidden items-center space-x-1 lg:flex">
              <TabButton
                icon={<Home size={18} />}
                label={t`Blueprints`}
                isActive={pathname.startsWith("/projects")}
                to="/projects"
              />
              {showFiles && (
                <TabButton
                  icon={<FileStack size={18} />}
                  label={t`Files`}
                  isActive={pathname.startsWith("/files")}
                  to="/files"
                />
              )}
              {showLayoutExplorer && (
                <TabButton
                  icon={<Search size={18} />}
                  label={t`Search`}
                  isActive={pathname.startsWith("/search")}
                  to="/search"
                />
              )}
              {showLayoutExplorer && (
                <TabButton
                  icon={<FileBarChart size={18} />}
                  label={t`Reports`}
                  isActive={pathname.startsWith("/reports")}
                  to="/reports"
                />
              )}
              {showBulkUpdates && (
                <TabButton
                  icon={<Layers size={18} />}
                  label={t`Bulk Updates`}
                  isActive={pathname.startsWith("/bulk-updates")}
                  to="/bulk-updates"
                />
              )}
              {showListOfMaterials && (
                <TabButton
                  icon={<ClipboardList size={18} />}
                  label={t`List of Materials`}
                  isActive={pathname.startsWith("/list-of-materials")}
                  to="/list-of-materials"
                />
              )}
              <TabButton
                icon={<Settings size={18} />}
                label={t`Settings`}
                isActive={pathname.startsWith("/settings")}
                to="/settings"
              />
              <TabButton
                icon={<HelpCircle size={18} />}
                label={t`Help`}
                isActive={pathname.startsWith("/docs")}
                to="/docs/en"
              />
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 md:gap-4 flex-shrink-0">
            <div className="flex items-center gap-1 sm:gap-2">
              {darkModeEnabled && <ModeToggle />}
              <SignedIn>
                <UserButton size="icon" additionalLinks={userButtonLinks} disableDefaultLinks />
              </SignedIn>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex-shrink-0">
              <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="p-2">
                {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                <span className="sr-only">{showMobileMenu ? t`Close menu` : t`Open menu`}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu backdrop and content - rendered outside flex container */}
      {showMobileMenu && (
        <>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events -- Backdrop click-outside pattern, keyboard users can press Escape */}
          <div
            data-mobile-backdrop
            className="fixed inset-0 z-[100] bg-black/50 transition-opacity duration-200"
            onClick={handleBackdropClick}
            aria-hidden="true"
          />

          {/* Mobile menu content */}
          <div
            className={cn(
              "fixed right-0 top-16 z-[110] h-[calc(100vh-4rem)] w-[min(18rem,85vw)] border-l border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900",
              "transform transition-transform duration-200 ease-out translate-x-0",
            )}
          >
            <div className="flex h-full flex-col p-4">
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-4 dark:border-gray-700">
                <BrandLogo variant="primary" size="md" />
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label={t`Close menu`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-1 flex-col gap-1">
                <MobileNavItem
                  icon={<Home size={18} />}
                  label={t`Blueprints`}
                  isActive={pathname.startsWith("/projects")}
                  to="/projects"
                  onClick={() => setShowMobileMenu(false)}
                />
                {showFiles && (
                  <MobileNavItem
                    icon={<FileStack size={18} />}
                    label={t`Files`}
                    isActive={pathname.startsWith("/files")}
                    to="/files"
                    onClick={() => setShowMobileMenu(false)}
                  />
                )}
                {showLayoutExplorer && (
                  <MobileNavItem
                    icon={<Search size={18} />}
                    label={t`Search`}
                    isActive={pathname.startsWith("/search")}
                    to="/search"
                    onClick={() => setShowMobileMenu(false)}
                  />
                )}
                {showLayoutExplorer && (
                  <MobileNavItem
                    icon={<FileBarChart size={18} />}
                    label={t`Reports`}
                    isActive={pathname.startsWith("/reports")}
                    to="/reports"
                    onClick={() => setShowMobileMenu(false)}
                  />
                )}
                {showBulkUpdates && (
                  <MobileNavItem
                    icon={<Layers size={18} />}
                    label={t`Bulk Updates`}
                    isActive={pathname.startsWith("/bulk-updates")}
                    to="/bulk-updates"
                    onClick={() => setShowMobileMenu(false)}
                  />
                )}
                {showListOfMaterials && (
                  <MobileNavItem
                    icon={<ClipboardList size={18} />}
                    label={t`List of Materials`}
                    isActive={pathname.startsWith("/list-of-materials")}
                    to="/list-of-materials"
                    onClick={() => setShowMobileMenu(false)}
                  />
                )}
                <MobileNavItem
                  icon={<Settings size={18} />}
                  label={t`Settings`}
                  isActive={pathname.startsWith("/settings")}
                  to="/settings"
                  onClick={() => setShowMobileMenu(false)}
                />
                <MobileNavItem
                  icon={<HelpCircle size={18} />}
                  label={t`Help`}
                  isActive={pathname.startsWith("/docs")}
                  to="/docs/en"
                  onClick={() => setShowMobileMenu(false)}
                />
              </nav>
            </div>
          </div>
        </>
      )}
    </header>
  )
}

// Theme toggle component using next-themes
function ModeToggle() {
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return (
      <button
        className="relative rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
        disabled
      >
        <div className="relative h-[1.2rem] w-[1.2rem]">
          <Monitor className="h-[1.2rem] w-[1.2rem] text-gray-600 dark:text-gray-400" />
        </div>
        <span className="sr-only">{t`Toggle theme`}</span>
      </button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800">
          <div className="relative h-[1.2rem] w-[1.2rem]">
            {theme === "system" ? (
              <Monitor className="h-[1.2rem] w-[1.2rem] text-gray-600 dark:text-gray-400" />
            ) : (
              <>
                <Sun className="absolute inset-0 h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="absolute inset-0 h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              </>
            )}
          </div>
          <span className="sr-only">{t`Toggle theme`}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => setTheme("light")}
          className={cn(theme === "light" && "bg-accent")}
        >
          <Sun className="mr-2 h-4 w-4" />
          {t`Light`}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("dark")}
          className={cn(theme === "dark" && "bg-accent")}
        >
          <Moon className="mr-2 h-4 w-4" />
          {t`Dark`}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("system")}
          className={cn(theme === "system" && "bg-accent")}
        >
          <Monitor className="mr-2 h-4 w-4" />
          {t`System`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function TabButton({
  icon,
  label,
  isActive,
  to,
}: {
  icon: React.ReactNode
  label: React.ReactNode
  isActive: boolean
  to: string
}) {
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center px-4 py-2 text-sm font-medium transition-colors",
        isActive ? "border-b-2 border-primary text-primary" : "text-primary hover:text-primary/80",
      )}
    >
      {icon}
      <span className="ml-2">{label}</span>
    </Link>
  )
}

TabButton.displayName = "TabButton"

function MobileNavItem({
  icon,
  label,
  isActive,
  to,
  onClick,
}: {
  icon: React.ReactNode
  label: React.ReactNode
  isActive: boolean
  to: string
  onClick?: () => void
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={cn(
        "flex items-center rounded-md px-2 py-2 text-sm font-medium transition-colors",
        isActive
          ? "bg-neutral-100 text-primary dark:bg-neutral-800"
          : "text-primary hover:bg-neutral-100 hover:text-primary/80 dark:hover:bg-neutral-800",
      )}
    >
      {icon}
      <span className="ml-2">{label}</span>
    </Link>
  )
}

MobileNavItem.displayName = "MobileNavItem"

function NavigationProgress({ isNavigating }: { isNavigating: boolean }) {
  if (!isNavigating) return null

  return (
    <div className="absolute left-0 right-0 top-0 h-0.5 overflow-hidden">
      <div
        className="bg-brand-orange-500 h-full w-full origin-left animate-[progress_1s_ease-in-out_infinite]"
        style={{
          animation: "progress 1s ease-in-out infinite",
        }}
      />
      <style>{`
        @keyframes progress {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  )
}

NavigationProgress.displayName = "NavigationProgress"
